// Tooltip - custom tooltip for [data-tooltip] elements: title/data-tooltip-content
// as content, optional [data-html] template reference, aria attributes.
// Behavioral port of v1 modules/Tooltip.js; positioning by @floating-ui/dom
// (replaces the deprecated tippy.js), rendering and interaction are our own.
//
// HTML contract (unchanged from v1): [data-tooltip], title, [data-html],
// [data-show-on-create]. Per-element overrides: [data-tooltip-placement],
// [data-tooltip-theme]. Legacy [data-tippy-content] is still read as a
// content fallback; new inits persist content to [data-tooltip-content].

import { attachFloating, type Placement } from './floating';

export interface TooltipConfig {
  /** Preferred placement; flips automatically when out of viewport. */
  placement?: Placement;
  /** Styling hook, rendered as data-theme on the tooltip box (light, success). */
  theme?: string;
  arrow?: boolean;
  /** Keep the tooltip open while the pointer is over it. */
  interactive?: boolean;
  /** Render content as HTML; when false, content is inserted as text. */
  allowHTML?: boolean;
  /** Show/hide delay in ms; a tuple sets [show, hide] separately. */
  delay?: number | [number, number];
  /** Distance from the reference element, px. */
  offset?: number;
  /** 'hover' shows on mouseenter/focus; 'manual' only via instance.show(). */
  trigger?: 'hover' | 'manual';
  /** Hide on click outside the tooltip. */
  hideOnClick?: boolean;
  /** Show immediately on init (implies manual trigger, no hideOnClick). */
  showOnCreate?: boolean;
  /** Override the .ms-tooltip stacking order (tippy's zIndex equivalent). */
  zIndex?: number;
  content?: (reference: HTMLElement) => string;
  /**
   * Called once the box is built, before it is shown - the hook modules like
   * FeatureHighlight use to enrich the tooltip (extra classes, a close button).
   */
  onMount?: (box: HTMLElement, instance: TooltipInstance) => void;
}

/** Programmatic handle for a single tooltip, kept in Tooltip#tooltips. */
export interface TooltipInstance {
  reference: HTMLElement;
  show(): void;
  hide(): void;
  destroy(): void;
}

// Must match the opacity transition in styles/modules/_tooltips.scss.
const TRANSITION_MS = 200;

const defaultConfig: TooltipConfig = {
  arrow: true,
  interactive: true,
  allowHTML: true,
  delay: 150,
  placement: 'top',
  offset: 8,
  trigger: 'hover',
  hideOnClick: true,
  content(reference) {
    // use title as a default tooltip content
    const { html } = reference.dataset;

    if (html) {
      const template = document.querySelector(html);
      if (!template) {
        console.error(`Tooltip: Element with selector "${html}" not found.`);
        return '';
      }
      reference.setAttribute('aria-describedby', html.replace('#', ''));
      return template.innerHTML;
    }

    const title =
      reference.getAttribute('title') ||
      reference.dataset.tooltipContent ||
      reference.dataset.tippyContent ||
      '';

    reference.removeAttribute('title');
    reference.setAttribute('data-tooltip-content', title);

    const htmlStripped = title.replace(/<[^>]*>?/gm, '');
    reference.setAttribute('aria-label', htmlStripped);

    return title;
  },
};

const delayPair = (delay: TooltipConfig['delay']): [number, number] =>
  Array.isArray(delay) ? delay : [delay ?? 0, delay ?? 0];

/**
 * One tooltip over one reference element. Exported so modules that are a
 * tooltip with extra behavior (FeatureHighlight, Clipboard) can compose it
 * instead of scanning the DOM through the Tooltip class.
 */
export class SingleTooltip implements TooltipInstance {
  reference: HTMLElement;

  private config: TooltipConfig;

  private content = '';

  private box: HTMLElement | null = null;

  private arrowEl: HTMLElement | null = null;

  private detachFloating: (() => void) | null = null;

  private showTimer: ReturnType<typeof setTimeout> | undefined;

  private hideTimer: ReturnType<typeof setTimeout> | undefined;

  private removeTimer: ReturnType<typeof setTimeout> | undefined;

  private visible = false;

  constructor(reference: HTMLElement, options: TooltipConfig = {}) {
    this.reference = reference;
    // Merged here too, not just in Tooltip - composing modules (FeatureHighlight,
    // Clipboard) instantiate this directly and must get the same defaults.
    this.config = { ...defaultConfig, ...options };

    const { config } = this;

    // Resolve content eagerly (v1/tippy behavior): moves title into
    // data-tooltip-content and sets aria attributes already on init.
    this.content = config.content ? config.content(reference) : '';

    if (config.trigger !== 'manual') {
      reference.addEventListener('mouseenter', this.scheduleShow);
      reference.addEventListener('focus', this.scheduleShow);
      reference.addEventListener('mouseleave', this.scheduleHide);
      reference.addEventListener('blur', this.scheduleHide);
      document.addEventListener('keydown', this.onKeydown);
    }
    if (config.hideOnClick !== false) {
      document.addEventListener('mousedown', this.onDocumentMousedown);
    }

    if (config.showOnCreate) {
      this.show();
    }
  }

  show = (): void => {
    clearTimeout(this.hideTimer);
    clearTimeout(this.removeTimer);
    if (this.visible) return;
    this.visible = true;

    const box = this.box ?? this.createBox();
    if (!box.isConnected) document.body.append(box);

    this.detachFloating?.();
    this.detachFloating = attachFloating(this.reference, box, {
      placement:
        (this.reference.dataset.tooltipPlacement as Placement | undefined) ??
        this.config.placement,
      offset: this.config.offset,
      arrowElement: this.arrowEl,
    });

    // Two frames so the browser paints data-state=hidden first and the
    // opacity transition actually runs.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.visible) box.setAttribute('data-state', 'visible');
      });
    });
  };

  hide = (): void => {
    clearTimeout(this.showTimer);
    if (!this.visible) return;
    this.visible = false;

    this.box?.setAttribute('data-state', 'hidden');
    this.detachFloating?.();
    this.detachFloating = null;

    clearTimeout(this.removeTimer);
    this.removeTimer = setTimeout(() => this.box?.remove(), TRANSITION_MS);
  };

  destroy(): void {
    clearTimeout(this.showTimer);
    clearTimeout(this.hideTimer);
    clearTimeout(this.removeTimer);
    this.detachFloating?.();
    this.detachFloating = null;

    this.reference.removeEventListener('mouseenter', this.scheduleShow);
    this.reference.removeEventListener('focus', this.scheduleShow);
    this.reference.removeEventListener('mouseleave', this.scheduleHide);
    this.reference.removeEventListener('blur', this.scheduleHide);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('mousedown', this.onDocumentMousedown);

    this.box?.remove();
    this.box = null;
    this.arrowEl = null;
    this.visible = false;
  }

  private createBox(): HTMLElement {
    const box = document.createElement('div');
    box.className = 'ms-tooltip';
    box.setAttribute('role', 'tooltip');
    box.setAttribute('data-state', 'hidden');

    const theme = this.reference.dataset.tooltipTheme ?? this.config.theme;
    if (theme) box.setAttribute('data-theme', theme);

    if (this.config.zIndex != null) box.style.zIndex = String(this.config.zIndex);

    const content = document.createElement('div');
    content.className = 'ms-tooltip__content';
    if (this.config.allowHTML !== false) {
      content.innerHTML = this.content;
    } else {
      content.textContent = this.content;
    }
    box.append(content);

    if (this.config.arrow !== false) {
      this.arrowEl = document.createElement('div');
      this.arrowEl.className = 'ms-tooltip__arrow';
      box.append(this.arrowEl);
    }

    if (this.config.interactive) {
      box.addEventListener('mouseenter', this.cancelHide);
      box.addEventListener('mouseleave', this.scheduleHide);
    }

    this.box = box;
    this.config.onMount?.(box, this);

    return box;
  }

  private scheduleShow = (): void => {
    clearTimeout(this.hideTimer);
    clearTimeout(this.showTimer);
    this.showTimer = setTimeout(this.show, delayPair(this.config.delay)[0]);
  };

  private scheduleHide = (): void => {
    clearTimeout(this.showTimer);
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(this.hide, delayPair(this.config.delay)[1]);
  };

  private cancelHide = (): void => {
    clearTimeout(this.hideTimer);
  };

  private onKeydown = (event: KeyboardEvent): void => {
    // WCAG 1.4.13: hover/focus content must be dismissable without moving
    // the pointer.
    if (event.key === 'Escape' && this.visible) this.hide();
  };

  private onDocumentMousedown = (event: MouseEvent): void => {
    if (!this.visible) return;
    const target = event.target as Node;
    if (this.box?.contains(target)) return;
    this.hide();
  };
}

export default class Tooltip {
  selector: string;

  config: TooltipConfig;

  tooltips = new Map<Element, TooltipInstance>();

  constructor(selector = '[data-tooltip]', config: TooltipConfig = {}) {
    this.config = { ...defaultConfig, ...config };
    this.selector = selector;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this._init);
    } else {
      this._init(); // Call directly if DOMContentLoaded has already occurred
    }
  }

  destroy(): void {
    this.tooltips.forEach((tooltip, target) => {
      tooltip.destroy();
      target.removeAttribute('aria-describedby'); // Clean up accessibility attributes
    });
    this.tooltips.clear();
  }

  update(): void {
    this.destroy();
    this._init();
  }

  private _init = (): void => {
    const targets = [...document.querySelectorAll<HTMLElement>(this.selector)];

    targets.forEach(target => {
      const targetConfig: TooltipConfig = {};
      const { showOnCreate } = target.dataset;

      if (showOnCreate) {
        targetConfig.showOnCreate = true;
        targetConfig.trigger = 'manual';
        targetConfig.hideOnClick = false;
      }

      const tooltipInstance = new SingleTooltip(target, { ...this.config, ...targetConfig });
      this.tooltips.set(target, tooltipInstance);
    });
  };
}
